import { HIERARCHY_TYPE } from '../constants/panelSourceConstants'

/**
 * Service for executing panelist aggregation queries
 * Used to determine default branch/panel selection based on panelist counts
 */

/**
 * Builds the filter object for aggregation query
 * Supports both branch hierarchy (non-AMP) and preferred panel hierarchy (AMP)
 * @param {Object} baseFilterObject - Base filter object from buildFilterObject
 * @param {Array} hierarchyIds - Array of IDs to aggregate (branch IDs or preferred panel IDs)
 * @param {string} hierarchyType - HIERARCHY_TYPE.BRANCH or HIERARCHY_TYPE.PREFERRED_PANEL
 * @returns {Object} - Filter object configured for aggregation
 */
export const buildAggregationFilterObject = (baseFilterObject, hierarchyIds, hierarchyType = HIERARCHY_TYPE.BRANCH) => {
  const aggregationFilterObject = { ...baseFilterObject }

  // Always remove branch_id and preferred_panel_id from aggregation queries
  // We want to count panelists across ALL branches/panels, not filter by selected one
  delete aggregationFilterObject.branch_id
  delete aggregationFilterObject.preferred_panel_id

  if (hierarchyType === HIERARCHY_TYPE.PREFERRED_PANEL) {
    // AMP users: aggregate by preferred panel IDs
    // Remove reggora_network_panel - not applicable for preferred panel aggregation
    delete aggregationFilterObject.reggora_network_panel
    aggregationFilterObject.aggregation_field = 'preferred_panel_ids'
  } else {
    // Non-AMP users: aggregate by branch IDs (default behavior)
    aggregationFilterObject.aggregation_field = 'branch_ids'
  }

  aggregationFilterObject.aggregation_values = hierarchyIds
  return aggregationFilterObject
}

/**
 * Executes panelist aggregation query
 * @param {Object} panelistAggregation - API resource for aggregation
 * @param {Object} filterObject - Filter object for the query
 * @returns {Promise<Object>} - Promise resolving to aggregation response
 */
export const executePanelistAggregation = async (panelistAggregation, filterObject) => {
  return panelistAggregation.post(filterObject)
}

/**
 * Extracts panelist counts from aggregation response
 * @param {Object} aggregationResponse - Response from aggregation API
 * @returns {Object} - Object mapping branch_id to panelist count
 */
export const extractPanelistCounts = (aggregationResponse) => {
  return aggregationResponse?.aggregation_results?.panelist_counts || {}
}
